// ---------------------------------------------------------------------------
// Alerts list — grouped by severity with actions
// ---------------------------------------------------------------------------

import { useFormatters } from '../hooks/useFormatters';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AlertSeverity = 'critical' | 'high' | 'medium' | 'low';
export type AlertStatus = 'active' | 'acknowledged' | 'resolved' | 'muted';

export interface AlertItem {
  id: string;
  ruleId: string;
  severity: AlertSeverity;
  message: string;
  currentValue: number;
  threshold: number;
  triggeredAt: string;
  status: AlertStatus;
  acknowledgedBy?: string;
  acknowledgedAt?: string;
}

interface AlertsListProps {
  alerts: AlertItem[];
  onAcknowledge?: (alertId: string) => void;
  onResolve?: (alertId: string) => void;
  onMute?: (alertId: string) => void;
}

// ---------------------------------------------------------------------------
// Severity config
// ---------------------------------------------------------------------------

const SEVERITY_CONFIG: Record<
  AlertSeverity,
  { label: string; icon: string; bgColor: string; textColor: string; borderColor: string; dotColor: string }
> = {
  critical: {
    label: 'Critical',
    icon: '!!',
    bgColor: 'bg-danger-tint',
    textColor: 'text-danger',
    borderColor: 'border-danger/30',
    dotColor: 'bg-danger-tint0',
  },
  high: {
    label: 'High',
    icon: '!',
    bgColor: 'bg-warning-tint',
    textColor: 'text-warning',
    borderColor: 'border-warning/30',
    dotColor: 'bg-warning',
  },
  medium: {
    label: 'Medium',
    icon: '-',
    bgColor: 'bg-warning-tint',
    textColor: 'text-warning',
    borderColor: 'border-warning/30',
    dotColor: 'bg-warning',
  },
  low: {
    label: 'Low',
    icon: 'i',
    bgColor: 'bg-primary-tint',
    textColor: 'text-primary',
    borderColor: 'border-primary/30',
    dotColor: 'bg-primary',
  },
};

const SEVERITY_ORDER: AlertSeverity[] = ['critical', 'high', 'medium', 'low'];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 1) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AlertsList({ alerts, onAcknowledge, onResolve, onMute }: AlertsListProps) {
  const f = useFormatters();
  // Group by severity
  const grouped = SEVERITY_ORDER.map((severity) => ({
    severity,
    config: SEVERITY_CONFIG[severity],
    items: alerts.filter((a) => a.severity === severity),
  })).filter((g) => g.items.length > 0);

  if (alerts.length === 0) {
    return (
      <div className="bg-surface-raised rounded-sm shadow p-8 text-center">
        <div className="w-12 h-12 rounded-sm bg-primary-tint flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className="text-sm font-medium text-ink">No active alerts</p>
        <p className="text-xs text-ink-muted mt-1">All systems are operating normally</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {grouped.map((group) => (
        <div key={group.severity}>
          <div className="flex items-center gap-2 mb-3">
            <span className={`w-2 h-2 rounded-sm ${group.config.dotColor}`} />
            <h3 className="text-sm font-semibold text-ink uppercase tracking-wider">
              {group.config.label} ({group.items.length})
            </h3>
          </div>
          <div className="space-y-2">
            {group.items.map((alert) => (
              <div
                key={alert.id}
                className={`rounded-sm border p-4 ${group.config.bgColor} ${group.config.borderColor}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${group.config.textColor}`}>
                      {alert.message}
                    </p>
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="text-xs text-ink-muted">
                        Value: {f.number(alert.currentValue)} (threshold: {f.number(alert.threshold)})
                      </span>
                      <span className="text-xs text-ink-muted">
                        {formatTimestamp(alert.triggeredAt)}
                      </span>
                    </div>
                    {alert.status === 'acknowledged' && alert.acknowledgedBy && (
                      <p className="text-xs text-ink-muted mt-1">
                        Acknowledged by {alert.acknowledgedBy}
                        {alert.acknowledgedAt ? ` at ${formatTimestamp(alert.acknowledgedAt)}` : ''}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 ms-4 flex-shrink-0">
                    {alert.status === 'active' && onAcknowledge && (
                      <button
                        type="button"
                        onClick={() => onAcknowledge(alert.id)}
                        className="px-2.5 py-1 text-xs font-medium text-ink bg-surface-raised border border-hairline-strong rounded-sm hover:bg-surface-sunken transition-colors"
                      >
                        Acknowledge
                      </button>
                    )}
                    {(alert.status === 'active' || alert.status === 'acknowledged') && onResolve && (
                      <button
                        type="button"
                        onClick={() => onResolve(alert.id)}
                        className="px-2.5 py-1 text-xs font-medium text-primary bg-primary-tint border border-primary/30 rounded-sm hover:bg-primary-tint transition-colors"
                      >
                        Resolve
                      </button>
                    )}
                    {alert.status === 'active' && onMute && (
                      <button
                        type="button"
                        onClick={() => onMute(alert.id)}
                        className="px-2.5 py-1 text-xs font-medium text-ink-muted bg-surface-sunken border border-hairline rounded-sm hover:bg-surface-sunken transition-colors"
                      >
                        Mute
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
